import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import ScrollIndicator from "@/components/landing/ScrollIndicator";
import heroBgVideo from "@/assets/diagnostic-hero-bg.mp4";

const DiagnosticHero = () => {
  const scrollToTest = () => {
    const testSection = document.getElementById("test");
    if (testSection) {
      testSection.scrollIntoView({
        behavior: "smooth"
      });
    }
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-black">
      {/* Background video */}
      <div className="absolute inset-0">
        <video 
          src={heroBgVideo} 
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full h-full object-cover"
        />
        {/* Burgundy gradient overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse 80% 60% at 50% 40%, rgba(96, 8, 50, 0.4) 0%, transparent 70%)"
          }}
        />
      </div>

      {/* Content */}
      <div className="container relative z-10 px-4 py-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="flex flex-col items-center text-center max-w-4xl mx-auto"
        >
          {/* Minimal badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-12"
          >
            <span className="text-xs tracking-[0.3em] uppercase text-neutral-500 font-medium">
              Test Capital de Marca · 3 min
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-display text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.1] mb-8 text-white tracking-tight"
          >
            ¿Cuánto vale tu
            <br />
            <span 
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: "linear-gradient(135deg, #FF9FBD 0%, #FCC3D7 100%)"
              }}
            >
              Capital de Marca
            </span>
            <span className="text-white">?</span>
          </motion.h1>

          {/* Elegant divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="w-16 h-px mb-8"
            style={{ background: "linear-gradient(to right, transparent, hsl(341, 100%, 81% / 0.5), transparent)" }}
          />

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="text-lg md:text-xl text-neutral-400 max-w-xl mb-14 leading-relaxed font-light"
          >
            Descubrí si tu trayectoria está generando{" "}
            <span className="text-white">patrimonio y valor</span>{" "}
            o si se está devaluando perdiendo activos..
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            <Button
              variant="gold"
              size="xl"
              onClick={scrollToTest}
              className="group uppercase tracking-wider font-semibold"
            >
              Iniciar Test
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>

          {/* Minimal trust indicator */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="mt-10 text-sm text-neutral-400 tracking-wide"
          >
            Recibí las respuestas de inmediato
          </motion.p>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
        <ScrollIndicator onClick={scrollToTest} />
      </div>
    </section>
  );
};

export default DiagnosticHero;
